import React, { useState, useEffect, useCallback, memo } from 'react';
// import { useEditorSettings } from '@/context/EditorSettingsContext';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, AlertTriangle, MessageCircle } from 'lucide-react';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { toast } from 'sonner';
import type { 
  Brand, 
  FirestoreLocation,
  Confirmation
} from '@/types/firestore';
import { useEntityName } from '@/hooks/useEntityName';
import { 
  DeleteDialog,
  LocationEditDialog, 
  BrandEditDialog, 
  ConfirmationEditDialog,
  DrinkEditDialog,
  CustomMapEditDialog,
  ReviewEditDialog
} from './dialogs';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { 
    EditableEntityType, 
    assessDeletionImpact,
    deleteEntity,
    startEditInteraction,
    startDeletionAssessment,
    cancelInteraction,
    selectEditorInteractionState,
    selectSelectedEntity,
    selectDeletionImpact,
    selectEditorOperationLoading,
    selectEditorOperationError,
    updateBrand,
    updateConfirmation
} from '@/store/slices/editorSlice';
import { updateLocation } from '@/store/slices/locationsSlice';
import { 
    selectHighlightStyle,
    selectHighlightColor,
    selectHighlightsEnabled
} from '@/store/slices/editorSettingsSlice';
import { deleteCustomMap } from '@/store/slices/customMapsSlice';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Textarea } from '@/components/ui/textarea';
import { updatePublicNote } from '@/store/slices/userSpecificSlice';

export type HighlightStyle = 'background' | 'border' | 'none';

interface EditorInteractableProps {
  children: React.ReactNode;
  entityId: string;
  entityType: EditableEntityType;
  authorUserId?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  highlightStyle?: HighlightStyle;
  requiredPermission?: string;
  sightingId?: string;
}

// Helper function to check if the current interactable matches the selected entity in the store
const isCurrentEntitySelected = (
    stateEntityType: EditableEntityType | null,
    stateEntityId: string | null,
    stateAuthorUserId: string | null,
    propsEntityType: EditableEntityType,
    propsEntityId: string,
    propsAuthorUserId?: string
): boolean => {
    const baseMatch = stateEntityType === propsEntityType && stateEntityId === propsEntityId;
    if (propsEntityType === 'visitedLocationNote') {
        return baseMatch && stateAuthorUserId === propsAuthorUserId;
    }
    return baseMatch;
};

// Define component without memo first and then wrap it
const EditorInteractableBase: React.FC<EditorInteractableProps> = ({
  children,
  entityId,
  entityType,
  authorUserId,
  onEdit,
  className = '',
  highlightStyle: propHighlightStyle,
  requiredPermission = 'editor',
  sightingId
}) => {
  const dispatch = useAppDispatch();

  // --- Selectors for RTK Editor State ---
  const interactionState = useAppSelector(selectEditorInteractionState);
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const deletionImpact = useAppSelector(selectDeletionImpact);
  const operationLoading = useAppSelector(selectEditorOperationLoading);
  const operationError = useAppSelector(selectEditorOperationError);
  
  // New state for custom map deletion alert dialog
  const [mapDeleteDialogOpen, setMapDeleteDialogOpen] = useState(false);
  const [mapToDelete, setMapToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // New state for note deletion/editing
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogMode, setNoteDialogMode] = useState<'edit' | 'delete'>('delete');
  const [noteToEdit, setNoteToEdit] = useState<{locationId: string, authorUserId: string, note: string} | null>(null);
  const [noteContent, setNoteContent] = useState('');
  
  // New state to track deletion dialog visibility and states
  const [showDeletionDialog, setShowDeletionDialog] = useState(false);
  
  // Check if *this specific* interactable instance is the one selected in the store
  const isSelected = isCurrentEntitySelected(
      selectedEntity?.type || null,
      selectedEntity?.id || null,
      selectedEntity?.authorUserId || null,
      entityType,
      entityId,
      authorUserId
  );

  // Conditionally fetch name only for valid, non-note EditableEntityTypes
  const isFetchableEntityType = [
      'brand', 'drink', 'location', 'sighting', 'user', 'customMap'
  ].includes(entityType);
  const { 
    name: fetchedName, 
    error: nameError 
  } = useEntityName(
    isFetchableEntityType ? (entityType as Exclude<EditableEntityType, 'visitedLocationNote' | 'confirmation'>) : null, 
    isFetchableEntityType ? entityId : null
  );
  // Provide a default name for specific types or if fetching fails/isn't applicable
  const entityName = 
      entityType === 'confirmation' ? `Confirmation (${entityId.substring(0, 6)}...)` 
    : entityType === 'visitedLocationNote' ? `Public Note by ${authorUserId?.substring(0,6) ?? '...'}`
    : fetchedName;

  const highlightStyle = useAppSelector(selectHighlightStyle);
  const highlightColor = useAppSelector(selectHighlightColor);
  const highlightsEnabled = useAppSelector(selectHighlightsEnabled);
  
  // React to name loading errors
  useEffect(() => {
    if (nameError) {
      console.error(`Error loading entity name for ${entityType} ${entityId}:`, nameError);
    }
  }, [nameError, entityType, entityId]);

  // Use prop-specific highlight style or fall back to the global one
  const effectiveHighlightStyle = propHighlightStyle || highlightStyle;
  
  // Memoize getColorClasses
  const getColorClasses = useCallback((color: string) => {
    switch (color) {
      case 'blue':
        return effectiveHighlightStyle === 'background' 
          ? 'bg-blue-500/30 hover:bg-blue-500/40 dark:bg-blue-800/30 dark:hover:bg-blue-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-blue-300 hover:border-blue-400 dark:border-blue-700 dark:hover:border-blue-600' 
            : '';
      case 'green':
         return effectiveHighlightStyle === 'background' 
          ? 'bg-green-500/30 hover:bg-green-500/40 dark:bg-green-800/30 dark:hover:bg-green-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-green-300 hover:border-green-400 dark:border-green-700 dark:hover:border-green-600' 
            : '';
      case 'purple':
         return effectiveHighlightStyle === 'background' 
          ? 'bg-purple-500/30 hover:bg-purple-500/40 dark:bg-purple-800/30 dark:hover:bg-purple-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-purple-300 hover:border-purple-400 dark:border-purple-700 dark:hover:border-purple-600' 
            : '';
      case 'orange':
         return effectiveHighlightStyle === 'background' 
          ? 'bg-orange-500/30 hover:bg-orange-500/40 dark:bg-orange-800/30 dark:hover:bg-orange-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-orange-300 hover:border-orange-400 dark:border-orange-700 dark:hover:border-orange-600' 
            : '';
      case 'red':
         return effectiveHighlightStyle === 'background' 
          ? 'bg-red-500/30 hover:bg-red-500/40 dark:bg-red-800/30 dark:hover:bg-red-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-red-300 hover:border-red-400 dark:border-red-700 dark:hover:border-red-600' 
            : '';
      // Default to blue if color not recognized
      default:
        return effectiveHighlightStyle === 'background' 
          ? 'bg-blue-500/30 hover:bg-blue-500/40 dark:bg-blue-800/30 dark:hover:bg-blue-800/40' 
          : effectiveHighlightStyle === 'border' 
            ? 'border border-blue-300 hover:border-blue-400 dark:border-blue-700 dark:hover:border-blue-600' 
            : '';
    }
  }, [effectiveHighlightStyle]);
  
  // const highlightClass = highlightsEnabled ? getColorClasses(highlightColor) : ''; // Will be moved and made conditional

  // --- Refactored Event Handlers ---

  // Handler for Edit menu item click
  const handleEditClick = useCallback(async () => {
    if (onEdit) {
      onEdit(); // Use custom handler if provided
      return;
    }
    
    // Special handling for visitedLocationNote
    if (entityType === 'visitedLocationNote') {
      if (!authorUserId) {
        toast.error("Author User ID is required to edit a public note.");
        return;
      }
      // Fetch the note content first
      try {
        const noteDocRef = doc(db, `users/${authorUserId}/visitedLocations`, entityId);
        const noteDocSnap = await getDoc(noteDocRef);
        if (noteDocSnap.exists() && noteDocSnap.data()?.publicNote) {
          setNoteContent(noteDocSnap.data()?.publicNote || '');
          setNoteToEdit({
            locationId: entityId,
            authorUserId: authorUserId,
            note: noteDocSnap.data()?.publicNote || ''
          });
          setNoteDialogMode('edit');
          setNoteDialogOpen(true);
        } else {
          toast.error("Could not find the note or it has been deleted.");
        }
      } catch (error) {
        console.error("Error fetching note:", error);
        toast.error("Failed to fetch note content for editing.");
      }
      return;
    }
    
    dispatch(startEditInteraction({ 
        entityType: entityType as EditableEntityType, 
        entityId 
    }));
  }, [entityType, entityId, authorUserId, onEdit, dispatch]);

  // Handler for Delete menu item click
  const handleDeleteClick = useCallback(async () => {
    if (!entityId) return;
    
    console.log(`[EditorInteractable] handleDeleteClick called for ${entityType} with ID: ${entityId}${authorUserId ? ` (Author: ${authorUserId})` : ''}`);
    
    // Close any edit dialog first if open for this entity
    if (isSelected && interactionState === 'editing') {
      dispatch(cancelInteraction());
    }

    if (entityType === 'confirmation' && !sightingId) {
      toast.error("Sighting ID is required to delete a confirmation.");
      return;
    }

    if (entityType === 'visitedLocationNote' && !authorUserId) {
      toast.error("Author User ID is required to delete a public note.");
      return;
    }
    
    // Special handling for custom maps - the context menu delete option
    if (entityType === 'customMap') {
      console.log(`[EditorInteractable] Opening custom map deletion dialog for ID: ${entityId}`);
      setMapToDelete(entityId);
      setMapDeleteDialogOpen(true);
      return;
    }
    
    // Special handling for public notes
    if (entityType === 'visitedLocationNote') {
      console.log(`[EditorInteractable] Opening note deletion dialog for user ${authorUserId}, location ${entityId}`);
      setNoteToEdit({
        locationId: entityId,
        authorUserId: authorUserId!,
        note: "" // We don't need the actual note content for deletion
      });
      setNoteDialogMode('delete');
      setNoteDialogOpen(true);
      return;
    }
    
    // Start deletion assessment, passing authorUserId if relevant
    dispatch(startDeletionAssessment({ 
      entityType: entityType as EditableEntityType,
      entityId, 
      authorUserId: (entityType as string) === 'visitedLocationNote' ? authorUserId : undefined
    }));
    
    // Only dispatch assessDeletionImpact if it's not a simple entity with no dependencies
    if ((entityType as string) !== 'visitedLocationNote' && 
        (entityType as string) !== 'customMap' && 
        (entityType as string) !== 'confirmation' &&
        (entityType as string) !== 'review') {
        dispatch(assessDeletionImpact({ 
          entityType: entityType as EditableEntityType,
          entityId 
        }));
    }

  }, [entityId, entityType, authorUserId, sightingId, dispatch, isSelected, interactionState]);

  // Handle custom map deletion confirmation
  const handleCustomMapDelete = useCallback(async () => {
    if (!mapToDelete) return;
    
    setIsDeleting(true);
    try {
      await dispatch(deleteCustomMap(mapToDelete)).unwrap();
      toast.success("Custom map deleted successfully");
    } catch (error: any) {
      console.error("Error deleting custom map:", error);
      toast.error(typeof error === 'string' ? error : "Failed to delete custom map");
    } finally {
      setIsDeleting(false);
      setMapDeleteDialogOpen(false);
      setMapToDelete(null);
    }
  }, [mapToDelete, dispatch]);

  // Handle note save/delete
  const handleNoteAction = useCallback(async () => {
    if (!noteToEdit) return;
    
    setIsDeleting(true);
    try {
      if (noteDialogMode === 'delete') {
        // Use the thunk for deletion (setting publicNote to null)
        await dispatch(updatePublicNote({ 
          authorUserId: noteToEdit.authorUserId,
          locationId: noteToEdit.locationId,
          note: null
        })).unwrap();
        toast.success("Public note removed successfully");
      } else {
        // Use the thunk for updating
        await dispatch(updatePublicNote({
          authorUserId: noteToEdit.authorUserId,
          locationId: noteToEdit.locationId,
          note: noteContent
        })).unwrap();
        toast.success("Public note updated successfully");
      }
    } catch (error: any) {
      console.error(`Error ${noteDialogMode === 'delete' ? 'deleting' : 'updating'} note:`, error);
      toast.error(`Failed to ${noteDialogMode === 'delete' ? 'delete' : 'update'} public note`);
    } finally {
      setIsDeleting(false);
      setNoteDialogOpen(false);
      setNoteToEdit(null);
      setNoteContent('');
    }
  }, [noteToEdit, noteDialogMode, noteContent, dispatch]);

  // Use showDeletionDialog exclusively for deletion confirmation
  useEffect(() => {
    // When an entity is selected for deletion, show the dialog immediately
    if (isSelected && (interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion')) {
      setShowDeletionDialog(true);
    } else {
      // Otherwise, make sure the dialog is closed
      setShowDeletionDialog(false);
    }
  }, [isSelected, interactionState]);

  // --- Dialog Callbacks (Dispatch RTK actions) ---
  const handleDialogClose = useCallback(() => {
      // Only cancel if we are in a relevant state for *this* entity
      if (isSelected && interactionState !== 'idle') {
          dispatch(cancelInteraction());
      }
      // Make sure to close the deletion dialog
      setShowDeletionDialog(false);
  }, [dispatch, isSelected, interactionState]);

  // Use this exclusively for the deletion dialog
  const handleDeletionDialogClose = useCallback(() => {
    console.log('[EditorInteractable] Canceling deletion dialog');
    // Only use Redux cancelInteraction to maintain consistent state
    dispatch(cancelInteraction());
  }, [dispatch]);

  // Log operation loading state changes
  useEffect(() => {
    console.log('[EditorInteractable] Operation loading state changed:', operationLoading);
  }, [operationLoading]);

  // Restore the deletion confirmation action
  const handleConfirmDeletionAction = useCallback(async (): Promise<void> => {
    console.log(`[EditorInteractable] handleConfirmDeletionAction called`, { selectedEntity });
    
    if (!selectedEntity) {
      console.error("[EditorInteractable] No entity selected for deletion. Using current entity properties.");
      // Fall back to the current entity properties
      if (!entityId) {
        toast.error("Cannot delete: No entity ID available");
        return;
      }
      
      try {
        console.log('[EditorInteractable] Starting deletion using current entity properties');
        await dispatch(deleteEntity({ 
          entityType: entityType as EditableEntityType,
          entityId,
          authorUserId,
          sightingId: entityType === 'confirmation' ? sightingId : undefined 
        })).unwrap();
        
        console.log('[EditorInteractable] Deletion successful');
        
        // Short delay before closing to show the loading state
        setTimeout(() => {
          toast.success(`${entityType} deleted successfully.`);
          // Use Redux cancelInteraction action to clear state properly
          dispatch(cancelInteraction());
        }, 500);
      } catch (error: any) {
        console.error(`Error deleting ${entityType}:`, error);
        toast.error(typeof error === 'string' ? error : `Failed to delete ${entityType}.`);
      }
      return;
    }
    
    try {
      console.log('[EditorInteractable] Starting deletion using selected entity properties');
      // Use the standard deleteEntity thunk for all types now
      await dispatch(deleteEntity({ 
        entityType: selectedEntity.type, 
        entityId: selectedEntity.id,
        authorUserId: selectedEntity.authorUserId || undefined,
        sightingId: selectedEntity.type === 'confirmation' ? sightingId : undefined 
      })).unwrap();
      
      console.log('[EditorInteractable] Deletion successful');
      
      // Short delay before closing to show the loading state
      setTimeout(() => {
        toast.success(`${selectedEntity.type === 'visitedLocationNote' ? 'Public Note' : selectedEntity.type} deleted successfully.`);
        // Use Redux cancelInteraction action to clear state properly
        dispatch(cancelInteraction());
      }, 500);
    } catch (error: any) { // Catch potential rejection from unwrap()
      console.error(`Error deleting ${selectedEntity.type}:`, error);
      toast.error(typeof error === 'string' ? error : `Failed to delete ${selectedEntity.type}.`);
      // Keep dialog open on error by not calling cancelInteraction
    }
  }, [dispatch, selectedEntity, entityId, entityType, authorUserId, sightingId]);

  // Determine assessment state for the unified dialog
  const getDeletionAssessmentState = useCallback(() => {
    if (operationError && interactionState === 'confirming_deletion') {
      return 'error';
    }
    // Access the correct property from the Redux store
    if (deletionImpact && deletionImpact.loading === 'pending') {
      return 'loading';
    }
    return 'completed';
  }, [deletionImpact, interactionState, operationError]);

  // --- Save Handlers (Need refactoring to dispatch thunks ideally) ---
  const handleSaveLocation = useCallback(async (locationData: Partial<FirestoreLocation>): Promise<void> => {
    // The 'operationLoading' state from editorSlice should ideally be set by the thunk
    // For now, we are just dispatching. Future step: ensure thunks update editorSlice.operationLoading
    try {
      await dispatch(updateLocation({ id: entityId, data: locationData })).unwrap();
      toast.success("Location updated successfully.");
      handleDialogClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating location:", error);
      // The thunk should ideally set an error message in the slice that can be displayed
      toast.error(typeof error === 'string' ? error : "Failed to update location.");
      // Keep dialog open on error
    }
  }, [entityId, dispatch, handleDialogClose]);

  const handleSaveBrand = useCallback(async (brandData: Partial<Brand>): Promise<void> => {
    try {
      await dispatch(updateBrand({ id: entityId, data: brandData })).unwrap();
      toast.success("Brand updated successfully.");
      handleDialogClose();
    } catch (error) {
      console.error("Error updating brand:", error);
      toast.error(typeof error === 'string' ? error : "Failed to update brand.");
      // Keep dialog open on error
    }
  }, [entityId, dispatch, handleDialogClose]);
  
  const handleSaveConfirmation = useCallback(async (confirmationData: Partial<Confirmation>): Promise<void> => {
    if (!sightingId) {
      toast.error("Sighting ID is missing, cannot save confirmation.");
      return;
    }
    if (!entityId) { // entityId is the confirmationId here
        toast.error("Confirmation ID is missing, cannot save confirmation.");
        return;
    }

    try {
      await dispatch(updateConfirmation({ 
        sightingId,
        confirmationId: entityId, 
        data: confirmationData 
      })).unwrap();
      toast.success("Confirmation updated successfully.");
      handleDialogClose();
    } catch (error) {
      console.error("Error updating confirmation:", error);
      toast.error(typeof error === 'string' ? error : "Failed to update confirmation.");
      // Keep dialog open on error
    }
  }, [entityId, sightingId, dispatch, handleDialogClose]);

  // --- Determine which Dialog to show ---
  let DialogComponent: React.ComponentType<any> | null = null;
  let dialogProps: any = {};

  if (isSelected && interactionState === 'editing' && entityType !== 'visitedLocationNote') {
    switch (entityType) {
      case 'location':
        DialogComponent = LocationEditDialog;
        dialogProps = { 
          open: true, 
          onOpenChange: handleDialogClose,
          onSave: handleSaveLocation 
        };
        break;
      case 'brand':
         DialogComponent = BrandEditDialog;
         dialogProps = { 
           open: true, 
           onOpenChange: handleDialogClose, 
           onSave: handleSaveBrand 
          };
        break;
      case 'drink':
         DialogComponent = DrinkEditDialog;
         dialogProps = { 
           open: true, 
           onOpenChange: handleDialogClose, 
        };
        break;
      case 'confirmation':
          DialogComponent = ConfirmationEditDialog;
          dialogProps = { 
            open: true, 
            onOpenChange: handleDialogClose, 
            onSave: handleSaveConfirmation,
            sightingId: sightingId
           };
          break;
      case 'customMap':
          DialogComponent = CustomMapEditDialog;
          dialogProps = {
            open: true,
            onOpenChange: handleDialogClose
          };
          break;
      case 'review':
          DialogComponent = ReviewEditDialog;
          dialogProps = {
            open: true,
            onOpenChange: handleDialogClose
          };
          break;
      default:
        console.warn(`Edit dialog not implemented for entity type: ${entityType}`);
    }
  }

  // --- Render Logic ---
  const menuEntityTypeLabel = entityType === 'visitedLocationNote' ? 'Public Note' : entityType;

  // Add debug logging for dialog state
  useEffect(() => {
    if (isSelected) {
      console.log('[EditorInteractable] Dialog state:', {
        interactionState,
        selectedEntity,
        deletionImpact,
        showDeletionDialog,
        operationLoading,
        operationError
      });
    }
  }, [
    isSelected, interactionState, selectedEntity, 
    deletionImpact, showDeletionDialog, operationLoading, operationError
  ]);

  return (
    <>
      <PermissionGuard requiredPermission={requiredPermission}>
        {(hasPermission) => {
          const highlightClass = hasPermission && highlightsEnabled ? getColorClasses(highlightColor) : '';
          return (
            <ContextMenu modal={false}>
              <ContextMenuTrigger 
                className={`${className} ${highlightClass} rounded-sm transition-colors duration-150 cursor-context-menu inline-block`}
                disabled={!hasPermission}
              >
                {children}
              </ContextMenuTrigger>
              <ContextMenuContent>
                {hasPermission && (
                  <>
                    <ContextMenuItem onClick={handleEditClick}>Edit {menuEntityTypeLabel}</ContextMenuItem>
                    <ContextMenuItem className="text-destructive" onClick={handleDeleteClick}>Delete {menuEntityTypeLabel}</ContextMenuItem>
                  </>
                )}
                {!hasPermission && (
                   <ContextMenuItem disabled>No permission</ContextMenuItem>
                )}
              </ContextMenuContent>
            </ContextMenu>
          );
        }}
      </PermissionGuard>

      {/* Only show edit dialogs here */}
      {DialogComponent && interactionState === 'editing' && <DialogComponent {...dialogProps} />} 

      {/* Always use the unified DeleteDialog for deletion */}
      {(showDeletionDialog || interactionState === 'confirming_deletion' || interactionState === 'assessing_deletion') && (
        <DeleteDialog
          isOpen={true}
          onClose={handleDeletionDialogClose}
          entityType={selectedEntity?.type || entityType}
          entityName={selectedEntity?.id 
            ? (entityName || (selectedEntity.type === 'visitedLocationNote' ? `Note by ${selectedEntity.authorUserId?.substring(0,6)}...` : selectedEntity.id))
            : (entityName || entityId)
          }
          assessmentState={getDeletionAssessmentState()}
          impactReport={deletionImpact.report}
          deletionState={operationLoading === 'pending' ? 'loading' : operationError ? 'error' : 'idle'}
          error={operationError}
          onConfirm={handleConfirmDeletionAction}
        />
      )}

      {/* Custom Map Delete Alert Dialog */}
      <AlertDialog open={mapDeleteDialogOpen} onOpenChange={setMapDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
              Delete Custom Map
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this custom map? This action cannot be undone
              and all locations saved to this map will be removed from it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCustomMapDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : 'Yes, Delete Map'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Public Note Edit/Delete Dialog */}
      <AlertDialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              {noteDialogMode === 'delete' ? (
                <>
                  <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
                  Delete Public Note
                </>
              ) : (
                <>
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Edit Public Note
                </>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {noteDialogMode === 'delete' 
                ? "Are you sure you want to delete this public note? This action cannot be undone."
                : "Edit the public note content below:"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {noteDialogMode === 'edit' && (
            <Textarea 
              value={noteContent} 
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Edit public note content..."
              className="min-h-[100px] text-sm resize-none my-2"
            />
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleNoteAction}
              disabled={isDeleting}
              className={noteDialogMode === 'delete' 
                ? "bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                : ""}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {noteDialogMode === 'delete' ? 'Deleting...' : 'Saving...'}
                </>
              ) : noteDialogMode === 'delete' ? 'Yes, Delete Note' : 'Save Changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// Export the memoized version
export const EditorInteractable = memo(EditorInteractableBase);
EditorInteractable.displayName = 'EditorInteractable';